import { useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Flag, Ban, MoreVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BlockUser, ReportUser } from "../services/backend";
import { REPORT_REASONS } from "../constants/datingCompliance";

export default function ReportBlockMenu({
  viewerId,
  targetUserId,
  targetName,
  onBlocked,
  variant = "icon",
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  if (!viewerId || !targetUserId || viewerId === targetUserId) return null;

  const handleBlock = async () => {
    setBusy(true);
    try {
      await BlockUser(viewerId, targetUserId);
      toast({
        title: t("safety.blockedTitle"),
        description: t("safety.blockedBody", { name: targetName || t("safety.thisUser") }),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      onBlocked?.(targetUserId);
    } catch (error) {
      toast({
        title: t("safety.actionFailed"),
        description: error.message || t("safety.tryAgain"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleReport = async () => {
    setBusy(true);
    try {
      await ReportUser({
        reporterId: viewerId,
        reportedId: targetUserId,
        reason,
        details: details.trim(),
      });
      toast({
        title: t("safety.reportedTitle"),
        description: t("safety.reportedBody"),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      setDetails("");
      onClose();
    } catch (error) {
      toast({
        title: t("safety.actionFailed"),
        description: error.message || t("safety.tryAgain"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Menu placement="bottom-end">
        {variant === "icon" ? (
          <MenuButton
            as={IconButton}
            aria-label={t("safety.moreActions")}
            icon={<MoreVertical size={18} />}
            size="sm"
            variant="ghost"
            borderRadius="full"
            color="whiteAlpha.900"
            bg="blackAlpha.500"
            _hover={{ bg: "blackAlpha.700" }}
            isDisabled={busy}
          />
        ) : (
          <MenuButton as={Button} size="sm" variant="outline" borderRadius="full" isDisabled={busy}>
            {t("safety.moreActions")}
          </MenuButton>
        )}
        <MenuList zIndex={40}>
          <MenuItem icon={<Flag size={16} />} onClick={onOpen}>
            {t("safety.report")}
          </MenuItem>
          <MenuItem icon={<Ban size={16} />} onClick={handleBlock} color="red.400">
            {t("safety.block")}
          </MenuItem>
        </MenuList>
      </Menu>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>{t("safety.reportTitle")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4} _dark={{ color: "whiteAlpha.700" }}>
              {t("safety.reportIntro", { name: targetName || t("safety.thisUser") })}
            </Text>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">{t("safety.reasonLabel")}</FormLabel>
              <Select value={reason} onChange={(e) => setReason(e.target.value)}>
                {REPORT_REASONS.map((key) => (
                  <option key={key} value={key}>
                    {t(`safety.reasons.${key}`)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t("safety.detailsLabel")}</FormLabel>
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={t("safety.detailsPlaceholder")}
                rows={3}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              {t("common.close")}
            </Button>
            <Button colorScheme="red" onClick={handleReport} isLoading={busy}>
              {t("safety.submitReport")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
